import ModalResponsive from '@/components/commons/modal-responsive';
import { CheckCircleIcon, PlusIcon, RefreshCcwIcon, SearchIcon } from 'lucide-react';
import clsx from 'clsx';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useGetNavigationsInfinite } from '@/hooks/api/dashboard/navigation';
import Cookies from 'js-cookie';
import { Input } from '@/components/ui/input';
import React from 'react';
import { useDebounceValue } from '@/hooks/use-debounce';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { useInView } from 'react-intersection-observer';

export type MenuType = {
  label: string;
  value: string;
};

export interface ModalSelectHeaderMenuProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  onSelect?: (subMenu: MenuType) => void;
  initialValues?: MenuType;
}

export default function ModalSelectHeaderMenu({
  openModal,
  setOpenModal,
  onSelect,
  initialValues,
}: ModalSelectHeaderMenuProps) {
  const [searchText, setSearchText] = React.useState('');
  const { storeId } = useParams<{ storeId: string }>();
  const isPartner = Cookies.get('_partner_id') !== undefined;
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 1,
  });
  const q = useDebounceValue(searchText, 500);

  const queryParams = {
    page: 1,
    pageSize: 10,
    q,
  };

  const {
    data: menus,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetNavigationsInfinite(queryParams);
  const navPath = isPartner
    ? `/${storeId}/site/navigation/create`
    : `/store/${storeId}/site/navigation/create`;

  React.useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  return (
    <>
      <ModalResponsive
        openModal={openModal}
        title='Select Menu'
        setOpenModal={(open) => {
          if (setOpenModal) setOpenModal(open);
          setSearchText('');
        }}
        className='max-w-md'
      >
        <div className='px-6'>
          <Input
            name='keyword'
            type='text'
            size={100}
            placeholder='Search'
            leftContent={<SearchIcon className='mx-auto w-4' />}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className='h-auto max-h-[400px] overflow-y-scroll pb-4'>
          <Link
            href={navPath}
            target='_blank'
            className={clsx(
              'mb-3 flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-100'
            )}
          >
            <div className='flex items-center gap-x-4 text-primary'>
              <PlusIcon className='h-4 w-4' />
              Create Menu
            </div>
            <RefreshCcwIcon className='h-4 w-4 text-primary' />
          </Link>
          {isLoading ? (
            <div className='flex w-full items-center justify-center py-2'>
              <LoadingAnimationIcon />
            </div>
          ) : (
            (menus as any)?.pages?.map((page: { data: any[] }, pageIndex: number) => (
              <div key={pageIndex}>
                {page.data.map((menu) => {
                  const selected = initialValues?.value === menu.id;
                  return (
                    <button
                      key={menu.id}
                      onClick={() => {
                        onSelect?.({ value: menu.id as string, label: menu.menu_name as string });
                        setOpenModal?.(false);
                      }}
                      className={clsx(
                        'group mb-3 flex w-full items-center justify-between px-6 py-1 text-left'
                      )}
                    >
                      {menu.title}
                      {selected ? (
                        <CheckCircleIcon className='h-5 w-5 text-primary' />
                      ) : (
                        <CheckCircleIcon className='h-5 w-5 opacity-0 duration-100 group-hover:opacity-100' />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
          <div ref={ref}>
            {isFetchingNextPage && (
              <div className='my-2 flex h-[15px] items-center justify-center gap-1'>
                <LoadingAnimationIcon />
                <span>Load More...</span>
              </div>
            )}
          </div>
        </div>
      </ModalResponsive>
    </>
  );
}
